// Reglas de la pestaña Referidos del cajón: estado, captura, orden, validación del dialog y la
// traducción de los codes del silo al campo que hay que corregir.
import { ApiError } from '@/api/errors';
import type {
    BackofficeCustomerReferral,
    BackofficeReferralCapture,
    BackofficeReferralSource,
    BackofficeReferralStatus,
    UpdateCustomerReferralInput,
} from '@/api/services/retail-wizard/customerReferrals.client';
import { formatDateMx } from '@/lib/dateHelpers';
import { fromStoredPhone, PHONE_MX_DIGITS } from '@/lib/phoneMx';
import { CUSTOMER_FALLBACK_NAME, ofCustomer } from '@/shared/lib/format/customerMention';
import type { PillTone } from '@/shared/patterns/Pills';

/** Permiso para agregar y corregir referidos desde el cajón. */
export const CUSTOMER_REFERRAL_WRITE_PERMISSION = 'retail.customer.referral.write';

export const REFERRAL_STATUS_LABEL: Record<BackofficeReferralStatus, string> = {
    NOT_PURCHASED: 'Aún no compra',
    ACCOUNT_OPENED: 'Abrió su cuenta',
    PURCHASED: 'Ya compró',
    SUREKEEP_CREDIT: 'Tiene crédito SureKeep',
};

/** Quién capturó, cuando el silo no resolvió su nombre. */
export const CAPTURED_BY_SOURCE_LABEL: Record<BackofficeReferralSource, string> = {
    BACKOFFICE: 'Admin de la cadena',
    SALE: 'Vendedor',
    OPENING: 'Vendedor (apertura)',
};

export const REFERRAL_STATUS_TONE: Record<BackofficeReferralStatus, PillTone> = {
    NOT_PURCHASED: 'neutral',
    ACCOUNT_OPENED: 'info',
    PURCHASED: 'success',
    SUREKEEP_CREDIT: 'success',
};

export const REFERRALS_TAB_COPY = {
    referredByTitle: 'Llegó recomendado por',
    listTitle: 'Sus referidos',
    count: (n: number) => (n === 1 ? '1 referido' : `${String(n)} referidos`),
    add: 'Agregar referido',
    edit: 'Editar',
    editLabel: (name: string) => `Editar a ${name}`,
    emptyTitle: 'Todavía no ha recomendado a nadie',
    emptyText:
        'Cuando este cliente recomiende a alguien en una venta o en su apertura de cuenta, aparecerá aquí.',
    emptyTextWrite:
        'Cuando este cliente recomiende a alguien en una venta o en su apertura de cuenta, aparecerá aquí. También puedes agregarlo tú.',
    footnote:
        'El premio en dinero siempre se deposita a la cuenta PCF de quien lo gana; el descuento se aplica en la compra. Los referidos no se borran; si un dato está mal, edítalo.',
    listLabel: 'Personas que recomendó este cliente',
    noCommission: 'Sin comisión al vendedor',
    noCommissionHint: 'Un referido agregado en el cajón no le genera comisión al vendedor.',
    noScopeTitle: 'Sin cadena: no se piden',
    noScopeText:
        'Los referidos se piden dentro de la cadena donde se hizo la venta, y el crédito de este cliente no la trae o no se pudo leer. No es que no tenga referidos.',
    forbiddenTitle: 'No tienes permiso para ver sus referidos',
    forbiddenText:
        'Tu rol no incluye los referidos de este cliente; contacta a quien administra los roles.',
    notFoundTitle: 'Este cliente no está en tu cadena',
    notFoundText:
        'No tiene ventas ni aperturas de cuenta en la cadena, así que sus referidos no se pueden ver.',
    scopeRequiredTitle: 'Falta elegir la cadena',
    scopeRequiredText: 'Los referidos se piden por cadena; elige una para verlos.',
    errorTitle: 'No se pudieron cargar sus referidos',
    errorText: 'Algo falló al leerlos. Intenta de nuevo en un momento.',
    retry: 'Reintentar',
} as const;

export const REFERRAL_DIALOG_COPY = {
    addTitle: 'Agregar referido',
    editTitle: 'Editar referido',
    addDescription: (customer: string) => `Una persona que ${customer} recomendó.`,
    editDescription: (customer: string) =>
        `Referido ${ofCustomer(customer)}. Su estado no cambia al editar.`,
    addNote:
        'Quedará marcado como agregado en el cajón a tu nombre. No genera comisión al vendedor.',
    editNote: 'Solo corriges nombre, teléfono y correo. Dónde y cuándo se capturó no cambia.',
    nameLabel: 'Nombre completo',
    namePlaceholder: 'Ej. Ana Sofía Martínez Ruiz',
    phoneLabel: 'Teléfono',
    phonePlaceholder: '10 dígitos',
    emailLabel: 'Correo (opcional)',
    emailPlaceholder: 'nombre@correo.com',
    cancel: 'Cancelar',
    submitAdd: 'Agregar referido',
    submitEdit: 'Guardar cambios',
    added: 'Referido agregado',
    updated: 'Referido actualizado',
    notFound: 'Este referido ya no está. Recargamos la lista; vuelve a intentarlo.',
    saveFailed: 'No pudimos guardar el referido. Intenta de nuevo.',
} as const;

const MSG = {
    nameRequired: 'Escribe el nombre del referido.',
    nameInvalid: 'Revisa el nombre.',
    phoneRequired: 'Escribe el teléfono del referido.',
    phoneLength: 'El teléfono debe tener 10 dígitos.',
    phoneInvalid: 'Revisa el teléfono; debe tener 10 dígitos.',
    isCustomer: 'Ese es el teléfono del propio cliente. Nadie puede recomendarse a sí mismo.',
    duplicate: 'Ese teléfono ya está en sus referidos. Revisa si es la misma persona.',
    duplicateOf: (name: string) =>
        `Ese teléfono ya está en sus referidos (${name}). Revisa si es la misma persona.`,
    emailInvalid: 'Revisa el correo; parece incompleto. Si no lo tienes, déjalo vacío.',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Lo capturado en el dialog; `phone` son solo dígitos (así lo entrega el `PhoneInput`). */
export interface ReferralForm {
    name: string;
    phone: string;
    email: string;
}

export type ReferralFormField = keyof ReferralForm;
export type ReferralFormErrors = Partial<Record<ReferralFormField, string>>;

/** Orden alfabético por nombre, sin distinguir mayúsculas ni acentos. */
export function sortByName<T extends { name: string }>(list: readonly T[]): T[] {
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
}

/** Dónde, dentro de la frase «Capturó … en …»: la venta o la apertura y su tienda, o el cajón. */
function capturePlace(capture: BackofficeReferralCapture): string {
    if (capture.source === 'BACKOFFICE') return 'el cajón';
    const what =
        capture.source === 'OPENING'
            ? 'apertura de cuenta'
            : capture.saleFolio
              ? `la venta ${capture.saleFolio}`
              : 'una venta';
    return capture.storeName ? `${what}, ${capture.storeName}` : what;
}

/** «Capturó <quién> en <dónde> · <fecha>». */
export function captureLine(capture: BackofficeReferralCapture): string {
    return `Capturó ${capturedByOf(capture)} en ${capturePlace(capture)} · ${formatDateMx(capture.capturedAt)}`;
}

/** Nombre de pila para los rótulos cortos; «el cliente» se queda entero. */
export function firstNameOf(name: string): string {
    if (name === CUSTOMER_FALLBACK_NAME) return name;
    return name.trim().split(/\s+/)[0] || name;
}

/** Quién lo capturó: su nombre o, si el silo no lo resolvió, la etiqueta de su origen. */
export function capturedByOf(capture: BackofficeReferralCapture): string {
    return capture.capturedByName?.trim() || CAPTURED_BY_SOURCE_LABEL[capture.source];
}

/** Lo editable de un referido guardado; el del wizard puede llegar sin nombre ni teléfono. */
export interface EditableReferral {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
}

export function formOf(referral: EditableReferral | null): ReferralForm {
    if (!referral) return { name: '', phone: '', email: '' };
    return {
        name: referral.name ?? '',
        phone: fromStoredPhone(referral.phone),
        email: referral.email ?? '',
    };
}

/** Errores locales del dialog; el teléfono del cliente puede venir con lada 52. */
export function validateReferralForm(
    form: ReferralForm,
    ctx: { customerPhone: string | null; others: readonly BackofficeCustomerReferral[] },
): ReferralFormErrors {
    const errors: ReferralFormErrors = {};
    if (!form.name.trim()) errors.name = MSG.nameRequired;
    if (!form.phone) errors.phone = MSG.phoneRequired;
    else if (form.phone.length !== PHONE_MX_DIGITS) errors.phone = MSG.phoneLength;
    else if (form.phone === fromStoredPhone(ctx.customerPhone)) errors.phone = MSG.isCustomer;
    else {
        const duplicate = ctx.others.find((other) => fromStoredPhone(other.phone) === form.phone);
        if (duplicate) errors.phone = MSG.duplicateOf(duplicate.name);
    }
    const email = form.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) errors.email = MSG.emailInvalid;
    return errors;
}

/** Solo lo que cambió; `null` si no cambió nada. Un correo vaciado viaja como `null`. */
export function referralPatchOf(
    original: EditableReferral,
    form: ReferralForm,
): UpdateCustomerReferralInput | null {
    const before = formOf(original);
    const patch: UpdateCustomerReferralInput = {};
    const name = form.name.trim();
    const email = form.email.trim();
    if (name !== before.name) patch.name = name;
    if (form.phone !== before.phone) patch.phone = form.phone;
    if (email !== before.email) patch.email = email || null;
    return Object.keys(patch).length > 0 ? patch : null;
}

/** El code del silo pegado al campo del dialog; `null` si no señala un campo. */
export function serverFieldError(
    error: unknown,
): { field: ReferralFormField; message: string } | null {
    if (!(error instanceof ApiError)) return null;
    const details = (error.data ?? {}) as { field?: unknown };
    switch (error.code) {
        case 'INVALID_REFERRAL_DATA':
            if (details.field === 'name') return { field: 'name', message: MSG.nameInvalid };
            if (details.field === 'phone') return { field: 'phone', message: MSG.phoneInvalid };
            if (details.field === 'email') return { field: 'email', message: MSG.emailInvalid };
            return null;
        case 'REFERRAL_IS_CUSTOMER':
            return { field: 'phone', message: MSG.isCustomer };
        case 'DUPLICATE_REFERRAL_PHONE':
            return { field: 'phone', message: MSG.duplicate };
        default:
            return null;
    }
}

export function isReferralNotFound(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'REFERRAL_NOT_FOUND';
}

export type ReferralsFailure = 'forbidden' | 'not-found' | 'scope-required' | 'error';

/** En qué quedó la lectura; un 403 o un 404 no se arreglan reintentando. */
/** Título, texto y si vale reintentar, por cada motivo de falla de la lectura. */
export const REFERRALS_FAILURE_COPY: Record<
    ReferralsFailure,
    { title: string; body: string; canRetry: boolean }
> = {
    forbidden: {
        title: REFERRALS_TAB_COPY.forbiddenTitle,
        body: REFERRALS_TAB_COPY.forbiddenText,
        canRetry: false,
    },
    'not-found': {
        title: REFERRALS_TAB_COPY.notFoundTitle,
        body: REFERRALS_TAB_COPY.notFoundText,
        canRetry: false,
    },
    'scope-required': {
        title: REFERRALS_TAB_COPY.scopeRequiredTitle,
        body: REFERRALS_TAB_COPY.scopeRequiredText,
        canRetry: false,
    },
    error: {
        title: REFERRALS_TAB_COPY.errorTitle,
        body: REFERRALS_TAB_COPY.errorText,
        canRetry: true,
    },
};

export function referralsFailureOf(error: unknown): ReferralsFailure | null {
    if (!error) return null;
    if (error instanceof ApiError) {
        if (error.code === 'PERMISSION_DENIED') return 'forbidden';
        if (error.code === 'RETAILER_SCOPE_REQUIRED') return 'scope-required';
        if (error.httpStatus === 404) return 'not-found';
    }
    return 'error';
}
